import { Checkbox, Stack, NumberField, Select, Option, InputGroup } from '@workramp/ui';
import pluralize from 'pluralize';
import { InputWrapper } from './RecurringAssignmentSetting.styles';

const {
  APP_CONSTANTS: { GROUP_AUTOMATION_UNITS },
} = window;

export const MIXPANEL_RECURRING_CONTENT_UPDATE = 'settings_repeat_assignment';
export const MIXPANEL_RECURRING_ASSIGNMENT_EXEMPTION_UPDATE = 'settings_repeat_assignment_exemption';

type IntervalUnits = 'day' | 'week' | 'month';
export type RepeatingAssignmentFieldNames =
  | 'isRepeatingAssignments'
  | 'numTimeUnitsUntilRepeat'
  | 'untilRepeatTimeUnitType'
  | 'numTimeUnitsDueAfterAssignment'
  | 'dueAfterAssignmentTimeUnitType';

export interface RecurringAssignmentProps {
  isRepeatingAssignments: boolean;
  numTimeUnitsUntilRepeat?: number;
  untilRepeatTimeUnitType?: IntervalUnits;
  numTimeUnitsDueAfterAssignment?: number;
  dueAfterAssignmentTimeUnitType?: IntervalUnits;
  checkboxDescription: string;
  onUpdate: (field: RepeatingAssignmentFieldNames, value: string | boolean | number | null) => void;
}

export const RecurringAssignmentSetting = ({
  isRepeatingAssignments,
  numTimeUnitsUntilRepeat,
  untilRepeatTimeUnitType,
  numTimeUnitsDueAfterAssignment,
  dueAfterAssignmentTimeUnitType,
  checkboxDescription,
  onUpdate,
}: RecurringAssignmentProps) => (
  <Stack size={4}>
    <Checkbox
      onChange={(value) => onUpdate('isRepeatingAssignments', value)}
      isSelected={isRepeatingAssignments}
      description={checkboxDescription}
    >
      Repeat assignment
    </Checkbox>

    <InputWrapper>
      <Stack size={4}>
        <InputGroup label="Reassign every">
          <NumberField
            hideLabel
            label="Reassign every"
            placeholder="Enter number"
            minValue={1}
            formatOptions={{ maximumFractionDigits: 0 }}
            value={numTimeUnitsUntilRepeat}
            onChange={(value) => onUpdate('numTimeUnitsUntilRepeat', value)}
            isDisabled={!isRepeatingAssignments}
          />
          <Select
            hideLabel
            label="Reassign every unit"
            title="Reassign every unit"
            placeholder="Select unit"
            isDisabled={!isRepeatingAssignments || !numTimeUnitsUntilRepeat}
            selectedKey={untilRepeatTimeUnitType}
            onSelectionChange={(value) => onUpdate('untilRepeatTimeUnitType', value)}
          >
            {Object.values(GROUP_AUTOMATION_UNITS).map((unit) => (
              <Option key={unit}>{pluralize(unit, numTimeUnitsUntilRepeat, false)}</Option>
            ))}
          </Select>
        </InputGroup>

        <InputGroup label="Due date after assignment">
          <NumberField
            hideLabel
            label="Due date after assignment"
            subLabel="(optional)"
            placeholder="Enter number"
            minValue={0}
            step={1}
            formatOptions={{ maximumFractionDigits: 0 }}
            value={numTimeUnitsDueAfterAssignment ?? undefined}
            onChange={(value) => {
              if (Number.isNaN(value)) {
                // Reset the due date unit select if the number is cleared
                onUpdate('numTimeUnitsDueAfterAssignment', null);
                onUpdate('dueAfterAssignmentTimeUnitType', null);
              } else {
                onUpdate('numTimeUnitsDueAfterAssignment', value);
              }
            }}
            isDisabled={!isRepeatingAssignments}
          />
          <Select
            hideLabel
            label="Due date after assignment unit"
            title="Due date after assignment unit"
            placeholder="Select unit"
            isDisabled={!isRepeatingAssignments || !numTimeUnitsDueAfterAssignment}
            selectedKey={dueAfterAssignmentTimeUnitType}
            onSelectionChange={(value) => onUpdate('dueAfterAssignmentTimeUnitType', value)}
          >
            {Object.values(GROUP_AUTOMATION_UNITS).map((unit) => (
              <Option key={unit}>{pluralize(unit, numTimeUnitsDueAfterAssignment, false)}</Option>
            ))}
          </Select>
        </InputGroup>
      </Stack>
    </InputWrapper>
  </Stack>
);
